import React from "react";
import { useAuth } from "../context/AuthContext";
import { useFunctionsPrevia } from "../hooks/useFunctionsPrevia";
import { useTranslation } from "react-i18next";
import NotificationsPortal, { notify } from "./NotificationsPortal";
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
  sendPasswordResetEmail,
  signOut,
} from "firebase/auth";
import { auth, db } from "../firebaseConfig";
import TwoFASmsCard from "./TwoFASmsCard";
import {
  collection,
  onSnapshot,
  doc,
  setDoc,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import Modal from "react-modal";
import {
  Eye,
  EyeOff,
  Check,
  Dot,
  AlertCircle,
  Lock,
  Mail,
  Smartphone,
} from "lucide-react";
import Sesiones from "./Sesiones";
import { getFunctions, httpsCallable } from "firebase/functions";

const pwdModalStyles = {
  overlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0,0,0,0.35)",
    display: "grid",
    placeItems: "center",
    zIndex: 1009,
    padding: 16,
  },
  content: {
    position: "relative",
    inset: "auto",
    width: 500,
    maxWidth: "92vw",
    background: "#fff",
    border: "0",
    borderRadius: 4,
    padding: "34px 44px 30px",
    boxShadow: "0 10px 40px rgba(0,0,0,0.25)",
    overflow: "hidden",
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "Inter, sans-serif",
    WebkitFontSmoothing: "antialiased",
    MozOsxFontSmoothing: "grayscale",
    color: "#111827",
  },
};

const actionCodeSettings = {
  url: `${window.location.origin}/login`, // o /auth/reset-completed
  handleCodeInApp: false, // usa la página hospedada de Firebase
};

export default function SeguridadPanel() {
  const { t } = useTranslation();
  const { user, sessionId } = useAuth();
  const { formatDate } = useFunctionsPrevia();
  const [reveal, setReveal] = React.useState({
    current: false,
    next: false,
    confirm: false,
  });
  const [lastPasswordUpdate, setLastPasswordUpdate] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [resetCooldown, setResetCooldown] = React.useState(0); // epoch ms
  const [resetCountdown, setResetCountdown] = React.useState(0);
  const [sessions, setSessions] = React.useState([]);
  const [showPwdForm, setShowPwdForm] = React.useState(false);
  const [pwd, setPwd] = React.useState({ current: "", next: "", confirm: "" });
  const [twoFA, setTwoFA] = React.useState({
    isEnrolled: false,
    enrolledLabel: "",
  });

  React.useEffect(() => {
    if (!user?.uid) return;

    // Escuchamos el documento del usuario para obtener la fecha real
    const userRef = doc(db, "users", user.uid);
    const unsubscribe = onSnapshot(userRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data.lastPasswordUpdate) {
          // Convertimos el Timestamp de Firebase a Date de JS
          setLastPasswordUpdate(data.lastPasswordUpdate.toDate());
        }
      }
    });

    return () => unsubscribe();
  }, [user]);

  React.useEffect(() => {
    if (!resetCooldown) return;
    const id = setInterval(() => {
      const remain = Math.max(
        0,
        Math.ceil((resetCooldown - Date.now()) / 1000),
      );
      setResetCountdown(remain);
      if (remain === 0) clearInterval(id);
    }, 250);
    return () => clearInterval(id);
  }, [resetCooldown]);

  React.useEffect(() => {
    if (!user?.uid) {
      setSessions([]);
      return;
    }

    const uid = user.uid;
    const colRef = collection(db, `users/${uid}/sessions`);

    const unsubscribe = onSnapshot(
      colRef,
      (snapshot) => {
        const docs = snapshot.docs
          .map((d) => {
            const data = d.data();
            let ultimaActividad = null;

            const v = data.ultimaActividad;
            if (v) {
              if (v.toDate) ultimaActividad = v.toDate();
              else if (typeof v === "number") ultimaActividad = new Date(v);
              else if (typeof v === "string") ultimaActividad = new Date(v);
            }

            const actual = d.id === sessionId;

            return { id: d.id, ...data, ultimaActividad, actual };
          })
          .filter((d) => !d.eliminado);

        // 1) actual primero 2) luego más reciente
        docs.sort((a, b) => {
          if (a.actual !== b.actual) return a.actual ? -1 : 1;
          const aT = a?.ultimaActividad?.getTime
            ? a.ultimaActividad.getTime()
            : 0;
          const bT = b?.ultimaActividad?.getTime
            ? b.ultimaActividad.getTime()
            : 0;
          return bT - aT;
        });

        setSessions(docs);
      },
      (err) => {
        // ✅ Si se ejecuta "Cerrar todas" → signOut → este listener puede alcanzar a disparar
        // permission-denied por un instante. No es un error real para el usuario.
        if (err?.code === "permission-denied") {
          setSessions([]);
          return;
        }

        console.error("Error escuchando sesiones:", err);
      },
    );

    return () => unsubscribe();
  }, [user?.uid, sessionId]);

  const pwdChecklist = React.useMemo(() => {
    const raw = pwd.next || "";
    const next = raw.trim();
    return {
      len8: next.length >= 8,
      letter: /[A-Za-z]/.test(next),
      number: /\d/.test(next),
      symbol: /[^A-Za-z0-9]/.test(next),
    };
  }, [pwd.next]);

  const handleSendReset = async () => {
    try {
      const now = Date.now();
      if (now < resetCooldown) return;

      if (!user?.email) {
        notify("error", "No hay email asociado a tu cuenta.");
        return;
      }

      // 1. Enviamos el correo de restablecimiento
      await sendPasswordResetEmail(auth, user.email, actionCodeSettings);

      // 2. 💾 ACTUALIZACIÓN: Guardamos la fecha del intento en Firestore
      // Esto hará que el panel muestre que la seguridad se movió hoy
      const userRef = doc(db, "users", user.uid);
      await setDoc(
        userRef,
        {
          lastPasswordUpdate: serverTimestamp(),
        },
        { merge: true },
      );

      setResetCooldown(now + 60_000); // 60s de cooldown
      notify("success", "Te enviamos un enlace de restablecimiento.");
    } catch (e) {
      // Manejo de error: No revelar si el correo existe por seguridad
      if (e.code === "auth/user-not-found") {
        setResetCooldown(Date.now() + 60_000);
        notify("success", "Te enviamos un enlace de restablecimiento.");
        return;
      }

      const map = {
        "auth/too-many-requests": "Demasiados intentos. Espera un momento.",
        "auth/invalid-email": "Email inválido.",
        "auth/network-request-failed": "Error de conexión.",
      };

      notify("error", map[e.code] || "No se pudo enviar el enlace.");
      console.error("[Reset Password Error]", e);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    const currentUser = auth.currentUser;

    if (!currentUser?.email) {
      notify("error", "No hay sesión activa.");
      return;
    }

    // ... (tus validaciones de coincidencia y checklist se mantienen igual)

    setSaving(true);
    try {
      // ⚙️ Reautenticar
      const cred = EmailAuthProvider.credential(currentUser.email, pwd.current);
      await reauthenticateWithCredential(currentUser, cred);

      // 🔑 Actualizar la contraseña en Firebase Auth
      await updatePassword(currentUser, pwd.next);

      // 💾 NUEVO: Guardar la fecha del cambio en Firestore
      // Usamos serverTimestamp para que la hora sea la del servidor y no la del reloj del PC del usuario
      const userRef = doc(db, "users", currentUser.uid);
      await setDoc(
        userRef,
        {
          lastPasswordUpdate: serverTimestamp(),
        },
        { merge: true },
      );

      // ✅ Confirmar visualmente
      notify("success", "Contraseña actualizada correctamente.");
      setShowPwdForm(false);
      setPwd({ current: "", next: "", confirm: "" });
    } catch (e) {
      console.error("[Change Password Error]", e);
      // ... (tus mensajes de error se mantienen igual)
    } finally {
      setSaving(false);
    }
  };

  const revoke = async (id) => {
    try {
      await updateDoc(doc(db, `users/${user.uid}/sessions/${id}`), {
        activo: false,
        eliminado: true,
        revokedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      notify("success", "Sesión cerrada correctamente.");
    } catch (err) {
      console.error("Error al cerrar la sesión:", err);
      notify("error", "No se pudo cerrar la sesión.");
    }
  };

  const revokeAllSessions = async () => {
    if (!user?.uid) return;

    try {
      const functions = getFunctions();
      const callRevokeAll = httpsCallable(functions, "revokeAllSessions");

      const resp = await callRevokeAll(); // { ok, globalLogoutAt }

      // ✅ Importantísimo: marca como visto ANTES de signOut
      const nowMs = resp?.data?.globalLogoutAt || Date.now();
      localStorage.setItem(`globalLogoutAtSeen:${user.uid}`, String(nowMs));

      // ✅ limpia sesión local
      localStorage.removeItem(`sessionId:${user.uid}`);
      localStorage.removeItem("mfaValidUntil");

      notify(
        "success",
        "Se cerraron todas las sesiones en todos los dispositivos.",
      );

      await signOut(auth);
    } catch (err) {
      console.error("Error al cerrar todas las sesiones:", err);
      notify("error", "No se pudieron cerrar todas las sesiones.");
    }
  };

  const closePwdModal = () => {
    setShowPwdForm(false);
    setPwd({ current: "", next: "", confirm: "" });
    setReveal({ current: false, next: false, confirm: false });
  };

  return (
    <>
      <NotificationsPortal />

      <div className="security-panel">
        {/* ===== Card: Cambiar contraseña ===== */}
        <div className="security-card">
          <div className="security-card-head">
            <div className="security-icon security-icon--blue">
              <Lock size={18} />
            </div>

            <div className="security-head-text">
              <div className="security-title">
                {t("security.password.cardTitle", "Cambiar contraseña")}
              </div>
              <div className="security-subtitle">
                {t(
                  "security.password.cardDesc",
                  "Actualiza tu contraseña regularmente para mantener tu cuenta segura",
                )}
              </div>
            </div>
          </div>

          <div className="security-divider" />

          <>
            <div className="security-pill">
              <span>
                {t("security.password.lastUpdate", "Última actualización")}:
              </span>
              <b>
                {lastPasswordUpdate
                  ? formatDate(lastPasswordUpdate)
                  : "Sin cambios recientes"}
              </b>
            </div>

            <div className="security-actions-row">
              <button
                type="button"
                className="security-btn-outline"
                onClick={handleSendReset}
                disabled={!!resetCountdown}
                title={
                  resetCountdown ? `Reenviar en ${resetCountdown}s` : undefined
                }
              >
                <Mail size={18} />
                <span>
                  {resetCountdown
                    ? `Reenviar en ${resetCountdown}s`
                    : t(
                        "security.password.sendReset",
                        "Enviar enlace de restablecimiento",
                      )}
                </span>
              </button>

              <button
                type="button"
                className="security-btn-primary"
                onClick={() => setShowPwdForm(true)}
              >
                {t("security.password.change", "Cambiar contraseña")}
              </button>
            </div>
          </>
        </div>

        {/* ===== Card: 2FA ===== */}
        <div className="security-card">
          <div className="security-card-head">
            <div className="security-icon security-icon--green">
              <Smartphone size={18} />
            </div>

            <div className="security-head-text">
              <div className="security-title">
                {t("security.2fa.title", "Autenticación de dos factores")}
              </div>
              <div className="security-subtitle">
                {twoFA.isEnrolled
                  ? "Protege tu cuenta con un código adicional al iniciar sesión"
                  : t(
                      "security.2fa.desc",
                      "Agrega una capa extra de seguridad a tu cuenta",
                    )}
              </div>
            </div>
          </div>

          <div className="security-divider" />
          <TwoFASmsCard onStatus={setTwoFA} />
        </div>

        {/* ===== Card: Sesiones ===== */}
        <Sesiones
          sessions={sessions}
          currentId={sessionId}
          revoke={revoke}
          revokeOthers={revokeAllSessions}
          t={t}
        />
      </div>

      <Modal
        isOpen={showPwdForm}
        onRequestClose={closePwdModal}
        shouldCloseOnOverlayClick
        shouldCloseOnEsc
        style={pwdModalStyles}
      >
        <h1
          style={{
            fontFamily: "Inter, sans-serif",
            textAlign: "center",
            fontWeight: 500,
            color: "#5b5b5b",
            margin: "8px 0 26px",
          }}
        >
          {t("security.password.cardTitle", "Cambiar contraseña")}
        </h1>

        <form
          onSubmit={handleChangePassword}
          style={{
            display: "grid",
            gap: 16,
          }}
        >
          {/* Contraseña actual */}
          <label style={{ display: "grid", gap: 8 }}>
            <span style={{ fontSize: 13, color: "#374151" }}>
              {t("security.password.current", "Contraseña actual")}
            </span>

            <div style={{ position: "relative" }}>
              <input
                type={reveal.current ? "text" : "password"}
                value={pwd.current}
                onChange={(e) =>
                  setPwd((p) => ({ ...p, current: e.target.value }))
                }
                required
                autoFocus
                style={{
                  width: "100%",
                  height: 44,
                  borderRadius: 6,
                  border: "1px solid #cfd6e3",
                  background: "#ffffff",
                  padding: "0 44px 0 14px",
                  fontSize: 14,
                  outline: "none",
                  minWidth: 0,
                  boxSizing: "border-box",
                }}
              />

              <button
                type="button"
                onClick={() =>
                  setReveal((r) => ({ ...r, current: !r.current }))
                }
                title={reveal.current ? "Ocultar" : "Mostrar"}
                style={{
                  position: "absolute",
                  right: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  width: 32,
                  height: 32,
                  borderRadius: 6,
                  border: "0",
                  background: "transparent",
                  cursor: "pointer",
                  color: "#374151",
                }}
              >
                {reveal.current ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </label>

          {/* Nueva contraseña */}
          <label style={{ display: "grid", gap: 8 }}>
            <span style={{ fontSize: 13, color: "#374151" }}>
              {t("security.password.new", "Nueva contraseña")}
            </span>

            {/* Wrapper SOLO para input + ojo */}
            <div style={{ position: "relative" }}>
              <input
                type={reveal.next ? "text" : "password"}
                value={pwd.next}
                onChange={(e) =>
                  setPwd((p) => ({ ...p, next: e.target.value }))
                }
                required
                style={{
                  width: "100%",
                  height: 44,
                  borderRadius: 6,
                  border: "1px solid #cfd6e3",
                  background: "#ffffff",
                  padding: "0 44px 0 14px", // deja espacio al ojo
                  fontSize: 14,
                  outline: "none",
                  minWidth: 0,
                  boxSizing: "border-box",
                }}
              />

              <button
                type="button"
                onClick={() => setReveal((r) => ({ ...r, next: !r.next }))}
                title={reveal.next ? "Ocultar" : "Mostrar"}
                style={{
                  position: "absolute",
                  right: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  width: 32,
                  height: 32,
                  borderRadius: 6,
                  border: "0",
                  background: "transparent",
                  cursor: "pointer",
                  color: "#374151",
                  display: "grid",
                  placeItems: "center",
                }}
              >
                {reveal.next ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            {/* Checklist FUERA del wrapper (ya no afecta el top:50% del ojo) */}
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 6 }}>
                La contraseña debe contener:
              </div>

              {[
                { ok: pwdChecklist.len8, text: "Al menos 8 caracteres" },
                {
                  ok: pwdChecklist.letter,
                  text: "1 letra (mayúscula o minúscula)",
                },
                {
                  ok: pwdChecklist.symbol,
                  text: "1 símbolo (ejemplo: !@#$%*&)",
                },
              ].map((r, idx) => (
                <div
                  key={idx}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    fontSize: 12,
                    color: r.ok ? "#16a34a" : "#9ca3af",
                    margin: "4px 0",
                  }}
                >
                  <span style={{ display: "grid", placeItems: "center" }}>
                    {r.ok ? <Check size={16} /> : <Dot size={16} />}
                  </span>

                  <span>{r.text}</span>
                </div>
              ))}
            </div>
          </label>

          {/* Confirmar */}
          <label style={{ display: "grid", gap: 8 }}>
            <span style={{ fontSize: 13, color: "#374151" }}>
              {t("security.password.confirm", "Confirmar nueva contraseña")}
            </span>

            <div style={{ position: "relative" }}>
              <input
                type={reveal.confirm ? "text" : "password"}
                value={pwd.confirm}
                onChange={(e) =>
                  setPwd((p) => ({ ...p, confirm: e.target.value }))
                }
                required
                style={{
                  width: "100%",
                  height: 44,
                  borderRadius: 6,
                  border: "1px solid #cfd6e3",
                  background: "#ffffff",
                  padding: "0 44px 0 14px",
                  fontSize: 14,
                  outline: "none",
                  minWidth: 0,
                  boxSizing: "border-box",
                }}
              />

              <button
                type="button"
                onClick={() =>
                  setReveal((r) => ({ ...r, confirm: !r.confirm }))
                }
                title={reveal.confirm ? "Ocultar" : "Mostrar"}
                style={{
                  position: "absolute",
                  right: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  width: 32,
                  height: 32,
                  borderRadius: 6,
                  border: "0",
                  background: "transparent",
                  cursor: "pointer",
                  color: "#374151",
                }}
              >
                {reveal.confirm ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            {pwd.confirm && pwd.confirm !== pwd.next && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: 10,
                  marginBottom: 6,
                }}
              >
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "10px 14px",
                    borderRadius: 8,
                    border: "1px solid #f4b4b4",
                    background: "#fdecec",
                    color: "#b42318",
                    fontSize: 13,
                    fontWeight: 500,
                    minWidth: 280,
                    justifyContent: "center",
                  }}
                  role="alert"
                  aria-live="polite"
                >
                  <AlertCircle size={16} />
                  <span>Las contraseñas no coinciden</span>
                </div>
              </div>
            )}
          </label>

          {/* Botones */}
          <div
            style={{
              marginTop: 10,
              display: "flex",
              justifyContent: "center",
              gap: 12,
            }}
          >
            <button
              type="submit"
              style={{
                height: 42,
                padding: "0 18px",
                borderRadius: 4,
                border: "0",
                backgroundColor: "var(--accent, #3B82F6)",
                color: "var(--accent-contrast, #fff)",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {t("security.password.change", "Cambiar contraseña")}
            </button>

            <button
              type="button"
              onClick={closePwdModal}
              disabled={saving}
              style={{
                height: 42,
                padding: "0 18px",
                borderRadius: 4,
                border: "0",
                background: "#6b7280",
                color: "#fff",
                fontWeight: 600,
                cursor: "pointer",
                opacity: saving ? 0.7 : 1,
              }}
            >
              {t("actions.cancel", "Cancelar")}
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
}
